import { useParams, Link } from "react-router-dom";
import { useState, useMemo } from "react";
import Layout from "@/components/feature/Layout";
import { categories, articles } from "@/mocks/data";
import JsonLd from "@/components/base/JsonLd";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || "plot-analysis");

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug]
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    window.history.replaceState(null, "", `/category/${newSlug}`);
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://www.example.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: currentCategory?.name || "分类",
      },
    ],
  };

  return (
    <Layout>
      <JsonLd data={breadcrumbSchema} />
      <section className="w-full pt-24 pb-6 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 font-medium">{currentCategory?.name || "分类"}</span>
          </nav>

          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {currentCategory?.name || "文章分类"}
            </h1>
            <p className="text-sm text-foreground-600">{currentCategory?.description}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200 border border-background-200/70"
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.articleCount})</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full py-10 md:py-14 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      title={article.title}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-3 py-1 rounded-full bg-background-50/90 backdrop-blur-sm text-xs font-medium text-primary-600">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 leading-relaxed mb-4 line-clamp-2">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>
                          {article.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i>
                          {article.readTime}
                        </span>
                      </div>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        {article.rating}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}